/**
 * SidebarSection — collapsible sidebar group with sticky label + chevron.
 * Werkruimte-sectie (collapsible=false, label=null) rendert alleen items.
 */

import { useEffect, type ReactNode } from 'react';
import clsx from 'clsx';
import { useSidebarSectionState } from '../../hooks/useSidebarSectionState';
import { ChevronIcon } from './sidebar-icons';
import type { NavSection } from './navigation';

interface SidebarSectionProps {
  section: NavSection;
  isActiveRoute: boolean;
  children: ReactNode;
}

export function SidebarSection({ section, isActiveRoute, children }: SidebarSectionProps) {
  const [open, toggle, setOpen] = useSidebarSectionState(section.id, section.defaultOpen);

  useEffect(() => {
    if (isActiveRoute && !open) {
      setOpen(true);
    }
  }, [isActiveRoute, open, setOpen]);

  if (!section.collapsible) {
    return (
      <div className="mb-2">
        {section.label && (
          <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            {section.label}
          </div>
        )}
        <ul className="space-y-1">{children}</ul>
      </div>
    );
  }

  const contentId = `sidebar-section-${section.id}`;

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={contentId}
        className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
      >
        <span>{section.label}</span>
        <ChevronIcon
          className={clsx(
            'w-3.5 h-3.5 transition-transform duration-150',
            open ? 'rotate-90' : 'rotate-0'
          )}
        />
      </button>
      {open && (
        <ul id={contentId} className="space-y-1">
          {children}
        </ul>
      )}
    </div>
  );
}
